// Note form component
import { useForm, Controller } from 'react-hook-form'
import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { noteSchema, type NoteInput, type Account, type Contact, type Lead, type Opportunity } from '../../../shared/types'
import { useTranslation } from '../../hooks/useTranslation'
import { Autocomplete, type AutocompleteOption } from '../ui/Autocomplete'
import { createCrudApi, type PaginatedResponse } from '../../lib/api'
import { OwnerSelect } from './OwnerSelect'

interface Props {
  defaultValues?: Partial<NoteInput>
  onSubmit: (data: NoteInput) => void
  onCancel?: () => void
  loading?: boolean
  relatedEntityName?: string
  canWriteField?: (field: string) => boolean
}

const accountsApi = createCrudApi<Account, unknown>('accounts')
const contactsApi = createCrudApi<Contact, unknown>('contacts')
const leadsApi = createCrudApi<Lead, unknown>('leads')
const opportunitiesApi = createCrudApi<Opportunity, unknown>('opportunities')

async function searchAccounts(search: string): Promise<AutocompleteOption[]> {
  const response: PaginatedResponse<Account> = await accountsApi.list({ filters: { name: search }, limit: 10 })
  return response.data.map((a) => ({ id: a.id, label: a.name }))
}

async function searchContacts(search: string): Promise<AutocompleteOption[]> {
  const response: PaginatedResponse<Contact> = await contactsApi.list({ filters: { lastName: search }, limit: 10 })
  return response.data.map((c) => ({ id: c.id, label: [c.firstName, c.lastName].filter(Boolean).join(' ') }))
}

async function searchLeads(search: string): Promise<AutocompleteOption[]> {
  const response: PaginatedResponse<Lead> = await leadsApi.list({ filters: { lastName: search }, limit: 10 })
  return response.data.map((l) => ({ id: l.id, label: [l.firstName, l.lastName].filter(Boolean).join(' ') }))
}

async function searchOpportunities(search: string): Promise<AutocompleteOption[]> {
  const response: PaginatedResponse<Opportunity> = await opportunitiesApi.list({ filters: { name: search }, limit: 10 })
  return response.data.map((o) => ({ id: o.id, label: o.name }))
}

const searchFunctions: Record<string, (search: string) => Promise<AutocompleteOption[]>> = {
  account: searchAccounts,
  contact: searchContacts,
  lead: searchLeads,
  opportunity: searchOpportunities,
}

export function NoteForm({ defaultValues, onSubmit, onCancel, loading, relatedEntityName, canWriteField = () => true }: Props) {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<NoteInput>({
    resolver: zodResolver(noteSchema),
    defaultValues,
  })

  const selectedType = watch('relatedToType')

  // Clear relatedToId when type changes (except on initial load)
  useEffect(() => {
    if (selectedType && selectedType !== defaultValues?.relatedToType) {
      setValue('relatedToId', '')
    }
  }, [selectedType, setValue, defaultValues?.relatedToType])

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700">
          {t('fields.title')}
        </label>
        <input
          {...register('title')}
          type="text"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="body" className="block text-sm font-medium text-gray-700">
          {t('fields.note')} <span className="text-red-500">*</span>
        </label>
        <textarea
          {...register('body')}
          rows={5}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        />
        {errors.body && (
          <p className="mt-1 text-sm text-red-600">{errors.body.message}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="relatedToType" className="block text-sm font-medium text-gray-700">
            {t('fields.relatedToType')} <span className="text-red-500">*</span>
          </label>
          <select
            {...register('relatedToType')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          >
            <option value="">{t('common.select')}</option>
            <option value="lead">{t('status.lead')}</option>
            <option value="account">{t('status.account')}</option>
            <option value="contact">{t('status.contact')}</option>
            <option value="opportunity">{t('status.opportunity')}</option>
          </select>
          {errors.relatedToType && (
            <p className="mt-1 text-sm text-red-600">{errors.relatedToType.message}</p>
          )}
        </div>

        <Controller
          name="relatedToId"
          control={control}
          render={({ field }) => (
            <Autocomplete
              value={field.value || ''}
              onChange={(id) => field.onChange(id)}
              fetchOptions={selectedType ? searchFunctions[selectedType] : async () => []}
              label={t('fields.relatedTo')}
              placeholder={selectedType ? `Search ${selectedType}s...` : 'Select type first...'}
              required
              error={errors.relatedToId?.message}
              minChars={2}
              queryKey={`${selectedType}-search`}
              initialLabel={relatedEntityName}
              disabled={!selectedType}
            />
          )}
        />
      </div>

      {/* Owner assignment - only visible to admins/managers */}
      <Controller
        name="ownerId"
        control={control}
        render={({ field }) => (
          <OwnerSelect
            value={field.value}
            onChange={field.onChange}
            disabled={loading}
          />
        )}
      />

      <div className="flex justify-end gap-3 pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {loading ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  )
}
